import { X } from "lucide-react";
import { observer } from "mobx-react-lite";

import { getOperatorDisplay } from "../config/schema-utils";
import {
  FilterChipLayoutVariant,
  resolveFilterChipLayout,
} from "../config/filter-value-type-behavior";
import { MAX_VISIBLE_FILTER_MULTI_VALUES } from "../config/constants";
import { getFilterDefinitionByKey } from "../config/structured-search-schema";
import { type Filter } from "../config/types";
import { useStructuredSearchStore } from "../stores";
import MultiValueBadges from "../FilterBuilderRow/MultiValueBadges";
import FilterValueRendererRouter from "../FilterValueRendererRouter";

import { getFilterChipLabel } from "./FilterChipLabel";

import { ProviderWithLogo } from "@/components/ProviderWithLogo";
import { mapProviderToDisplayName } from "@/components/ProviderDropdown/utils";
import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { useTruncatedList } from "@/hooks/useTruncatedList";
import { cn } from "@/lib/utils";

interface FilterChipProps {
  filter: Filter;
  onRemove: () => void;
  onEdit: () => void;
  selectedForRemoval?: boolean;
  isEditing?: boolean;
  isLocked?: boolean;
}

const ProviderValueList = ({ values }: { values: string[] }) => {
  const recalcKey = values.join("|");
  const { containerRef, measureRef, visibleCount, hiddenCount } = useTruncatedList({
    itemCount: values.length,
    keepMinOne: true,
    overflowIndicatorWidth: 40,
    maxVisibleItems: MAX_VISIBLE_FILTER_MULTI_VALUES,
    recalcKey,
  });
  const hiddenLabels = values
    .slice(visibleCount)
    .map(value => mapProviderToDisplayName(value))
    .join(", ");

  return (
    <span
      ref={containerRef}
      className="relative flex min-w-0 max-w-full flex-nowrap items-center gap-1 overflow-hidden"
    >
      <span
        ref={measureRef}
        className="pointer-events-none invisible absolute flex flex-nowrap items-center gap-1"
        aria-hidden="true"
      >
        {values.map(value => (
          <span key={value} className="shrink-0">
            <ProviderWithLogo provider={value} size="sm" />
          </span>
        ))}
      </span>
      {values.slice(0, visibleCount).map(value => (
        <span key={value} className="min-w-0 shrink-0">
          <ProviderWithLogo provider={value} size="sm" />
        </span>
      ))}
      {hiddenCount > 0 && (
        <BasicTooltip content={hiddenLabels}>
          <span>
            <Badge
              variant={BadgeVariant.GRAY}
              size={BadgeSize.SM}
              className="pointer-events-none border-slate-200 from-slate-50 to-slate-50 text-slate-600 shadow-none"
            >
              +{hiddenCount}
            </Badge>
          </span>
        </BasicTooltip>
      )}
    </span>
  );
};

const FilterChip = observer(
  ({ filter, onRemove, onEdit, selectedForRemoval, isEditing, isLocked }: FilterChipProps) => {
    const store = useStructuredSearchStore();
    const filterDefinition = getFilterDefinitionByKey(store.schema, filter.field);
    const rawDisplayName = filterDefinition?.label ?? filter.field;
    const displayName = filter.nestedKey
      ? `${rawDisplayName} [${filter.nestedKey}]`
      : rawDisplayName;
    const operatorLabel = getOperatorDisplay(store.schema, {
      operator: filter.operator,
      field: filter.field,
      nestedKey: filter.nestedKey,
    });

    const label = getFilterChipLabel(filter, store.schema);
    const isMultiValueFilter =
      Array.isArray(filter.value) && (filter.operator === "in" || filter.operator === "not_in");
    let chipContent: React.ReactNode;
    const chipVariant = resolveFilterChipLayout(filterDefinition, isMultiValueFilter);

    switch (chipVariant) {
      case FilterChipLayoutVariant.Provider: {
        const providerValues = Array.isArray(filter.value)
          ? filter.value.map(value => String(value))
          : [String(filter.value)];

        chipContent = (
          <span className="flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden">
            <span className="shrink-0">{displayName}</span>
            <span className="shrink-0">{operatorLabel}</span>
            {Array.isArray(filter.value) ? (
              <span className="flex min-w-0 shrink items-center overflow-hidden">
                <span className="shrink-0">(</span>
                <span className="min-w-0 shrink overflow-hidden">
                  <ProviderValueList values={providerValues} />
                </span>
                <span className="shrink-0">)</span>
              </span>
            ) : (
              <span className="min-w-0">
                <ProviderWithLogo provider={providerValues[0] ?? ""} size="sm" />
              </span>
            )}
          </span>
        );
        break;
      }

      case FilterChipLayoutVariant.Status:
        chipContent = (
          <span className="flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden">
            <span className="shrink-0">{displayName}</span>
            <span className="shrink-0">{operatorLabel}</span>
            {Array.isArray(filter.value) ? (
              <span className="flex min-w-0 shrink items-center overflow-hidden">
                <span className="shrink-0">(</span>
                <span className="min-w-0 shrink overflow-hidden">
                  <MultiValueBadges
                    values={filter.value.map(status => String(status))}
                    definition={filterDefinition}
                    maxVisibleItems={MAX_VISIBLE_FILTER_MULTI_VALUES}
                  />
                </span>
                <span className="shrink-0">)</span>
              </span>
            ) : (
              <FilterValueRendererRouter
                definition={filterDefinition}
                value={String(filter.value)}
                className="pointer-events-none"
              />
            )}
          </span>
        );
        break;

      case FilterChipLayoutVariant.MultiValue:
        chipContent = (
          <span className="flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden">
            <span className="shrink-0">{displayName}</span>
            <span className="shrink-0">{operatorLabel}</span>
            <span className="flex min-w-0 shrink items-center overflow-hidden">
              <span className="shrink-0">(</span>
              <span className="min-w-0 shrink overflow-hidden">
                <MultiValueBadges
                  values={
                    Array.isArray(filter.value)
                      ? filter.value.map(value => String(value))
                      : [String(filter.value)]
                  }
                  displayValues={filter.multiValueDisplay}
                  badgeClassName="pointer-events-none border-slate-200 from-slate-50 to-slate-50 text-slate-700 shadow-none"
                  textClassName="max-w-[140px] truncate"
                  overflowIndicatorWidth={32}
                  maxVisibleItems={MAX_VISIBLE_FILTER_MULTI_VALUES}
                />
              </span>
              <span className="shrink-0">)</span>
            </span>
          </span>
        );
        break;

      default:
        chipContent = <span className="max-w-full truncate">{label}</span>;
    }

    return (
      <div
        data-structured-search-chip="true"
        role={isLocked ? undefined : "button"}
        tabIndex={isLocked ? undefined : 0}
        onClick={isLocked ? undefined : onEdit}
        onKeyDown={e => {
          if (isLocked) {
            return;
          }
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onEdit();
          }
        }}
        className={cn(
          "inline-flex min-h-7 max-w-full cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 shadow-sm shadow-slate-200/60 transition-colors hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none",
          isLocked && "cursor-default bg-slate-50 hover:border-slate-200 hover:bg-slate-50",
          selectedForRemoval && "border-blue-300 bg-blue-50 text-blue-700 ring-offset-1",
          isEditing &&
            "border-blue-200 bg-blue-50 text-blue-700 shadow-sm shadow-blue-100/80 hover:border-blue-300 hover:bg-blue-100",
        )}
      >
        <OverflowDisplayTooltip content={label}>
          <span className="flex min-w-0 max-w-full items-center overflow-hidden">
            {chipContent}
          </span>
        </OverflowDisplayTooltip>
        {!isLocked && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onRemove();
            }}
            className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-200/70 hover:text-slate-600"
            aria-label={`Remove ${label} filter`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  },
);

export default FilterChip;
